/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import Page from "components/common_components/Page";
import Button from "components/common_components/Button";
import Icon from "components/common_components/Icon";
import Status from "components/common_components/Status";
import LoadingPage from "components/common_components/LoadingPage";
import { ConfirmDialog } from "components/common_components/Dialogs";
import { Form } from "components/common_components/Formik";
import { notifyError, notifySuccess, notifyWarning } from "redux/actions/Notifications";
import CampaignsContext from "./CampaignsContext";
import CampaignBasicFields from "./components/CampaignBasicFields";
import AssetsSection from "./components/AssetsSection";
import { buildCloneStateFromCampaign } from "./utils";

function ViewCampaign() {
    const { id } = useParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const { Campaigns, Storage, permissions, setCloneState } = useContext(CampaignsContext);

    const [isLoading, setIsLoading] = useState(false);
    const [campaign, setCampaign] = useState(undefined);
    const [loaders, setLoaders] = useState({ inactive: false, activate: false, delete: false });
    const [confirmDialogProps, setConfirmDialogProps] = useState({
        open: false,
        title: "",
        content: "",
        primaryButtonText: "",
        onPrimaryClick: undefined,
        secondaryButtonText: "Cancel",
        onSecondaryClick: () => setConfirmDialogProps((prev) => ({ ...prev, open: false })),
        onClose: () => setConfirmDialogProps((prev) => ({ ...prev, open: false })),
    });

    const getCampaign = () => {
        setIsLoading(true);
        Campaigns.getOneById({
            id,
            onSuccess: (data) => {
                setCampaign(data);
                setIsLoading(false);
            },
            onError: (error) => {
                dispatch(notifyError(error.message || error));
                setIsLoading(false);
                history.push("/campaigns");
            },
        });
    };

    useEffect(() => {
        if (!id) {
            dispatch(notifyError("No ID included in the path."));
            history.push("/campaigns");
            return;
        }
        getCampaign();
        return () => setCampaign(undefined);
    }, [id]);

    const initialValues = campaign
        ? {
              name: campaign.name || "",
              description: campaign.description || "",
              startDatetime: campaign.startDatetime || null,
              endDatetime: campaign.endDatetime || null,
              assets: (campaign.assets || []).map((asset) => ({
                  id: asset.id || null,
                  text1: asset.text1 || "",
                  text2: asset.text2 || "",
                  text3: asset.text3 || "",
                  imageUrl: asset.imageUrl || "",
                  onImageClickUrl: asset.onImageClickUrl || "",
              })),
          }
        : {};

    const onCancel = () => history.push("/campaigns");
    const onEdit = () => history.push(`/campaigns/${id}/edit`);

    const onClone = () => {
        setCloneState(buildCloneStateFromCampaign(campaign));
        history.push("/campaigns/add");
    };

    const canInactivate = permissions.edit && campaign?.status === "ACTIVE";
    const canActivate =
        permissions.edit && campaign?.status === "INACTIVE" && campaign?.endDatetime && new Date(campaign.endDatetime) > new Date();

    const onActivate = () => {
        setConfirmDialogProps((prev) => ({
            ...prev,
            open: true,
            title: "Activate campaign",
            content: "Are you sure you want to reactivate this campaign? It will be shown again in the Client Portal.",
            primaryButtonText: "Yes, activate",
            onPrimaryClick: async () => {
                try {
                    setLoaders((prev) => ({ ...prev, activate: true }));
                    await Campaigns.setActiveAsync(id);
                    dispatch(notifySuccess("Campaign activated."));
                    getCampaign();
                } catch (error) {
                    dispatch(notifyError(error.response?.data?.message || error.message || "Unexpected error."));
                } finally {
                    setLoaders((prev) => ({ ...prev, activate: false }));
                    setConfirmDialogProps((prev) => ({ ...prev, open: false }));
                }
            },
        }));
    };

    const onInactivate = () => {
        setConfirmDialogProps((prev) => ({
            ...prev,
            open: true,
            title: "Mark campaign as inactive",
            content: "Are you sure you want to mark this campaign as inactive? It will no longer be shown in the Client Portal.",
            primaryButtonText: "Yes, mark inactive",
            onPrimaryClick: async () => {
                try {
                    setLoaders((prev) => ({ ...prev, inactive: true }));
                    await Campaigns.setInactiveAsync(id);
                    dispatch(notifyWarning("Campaign marked as inactive."));
                    getCampaign();
                } catch (error) {
                    dispatch(notifyError(error.response?.data?.message || error.message || "Unexpected error."));
                } finally {
                    setLoaders((prev) => ({ ...prev, inactive: false }));
                    setConfirmDialogProps((prev) => ({ ...prev, open: false }));
                }
            },
        }));
    };

    const onDelete = () => {
        setConfirmDialogProps((prev) => ({
            ...prev,
            open: true,
            title: "Delete campaign",
            content: "Are you sure you want to delete this campaign and all its assets? This action cannot be undone.",
            primaryButtonText: "Yes, delete",
            onPrimaryClick: () => {
                setLoaders((prev) => ({ ...prev, delete: true }));
                Campaigns.deleteAll({
                    ids: [Number(id)],
                    onSuccess: () => {
                        setLoaders((prev) => ({ ...prev, delete: false }));
                        setConfirmDialogProps((prev) => ({ ...prev, open: false }));
                        dispatch(notifySuccess("Campaign deleted successfully!"));
                        history.push("/campaigns");
                    },
                    onError: (error) => {
                        setLoaders((prev) => ({ ...prev, delete: false }));
                        setConfirmDialogProps((prev) => ({ ...prev, open: false }));
                        dispatch(notifyError(error.message || error));
                    },
                });
            },
        }));
    };

    return (
        <Page className="campaign-form-page campaign-view-page">
            {isLoading ? (
                <LoadingPage />
            ) : (
                campaign && (
                    <>
                        <div className="form-head">
                            <div className="item back">
                                <Button secondary link small onClick={onCancel}>
                                    <Icon name="arrow" size="sm" color="secondary" />
                                    <span>Back</span>
                                </Button>
                            </div>
                            <div className="item form-status">
                                <Status id={campaign.status} withIcon />
                            </div>
                            <div className="item title">
                                <h2>{campaign.name}</h2>
                            </div>
                            <div className="item form-actions">
                                {permissions.add && (
                                    <Button secondary small onClick={onClone}>
                                        <Icon name="add-file" size="md" color="white" />
                                        <span>Clone</span>
                                    </Button>
                                )}
                                {permissions.edit && (
                                    <Button secondary small onClick={onEdit}>
                                        <Icon name="pencil" size="md" color="white" />
                                        <span>Edit</span>
                                    </Button>
                                )}
                                {canActivate && (
                                    <Button secondary outline small onClick={onActivate} loading={loaders.activate}>
                                        <Icon name="rounded-check" size="sm" color="secondary" />
                                        <span>Activate</span>
                                    </Button>
                                )}
                                {canInactivate && (
                                    <Button secondary outline small onClick={onInactivate} loading={loaders.inactive}>
                                        <Icon name="cancel" size="xs" color="secondary" />
                                        <span>Inactivate</span>
                                    </Button>
                                )}
                                {permissions.delete && (
                                    <Button secondary link small onClick={onDelete} loading={loaders.delete}>
                                        <Icon name="delete" size="sm" color="secondary" />
                                        <span>Delete</span>
                                    </Button>
                                )}
                            </div>
                        </div>

                        <div className="campaign-form">
                            <Form enableReinitialize initialValues={initialValues} showButtons={false} onSubmit={() => {}}>
                                <CampaignBasicFields disabled />
                                <AssetsSection campaignId={id} Storage={Storage} disabled />
                            </Form>
                        </div>

                        <ConfirmDialog {...confirmDialogProps} />
                    </>
                )
            )}
        </Page>
    );
}

export default ViewCampaign;
